import { useCallback, useEffect, useState } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError } from '@/components/primitives'
import { LoadingRegion, Skeleton, SkeletonText } from '@/components/states'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate } from '@/lib/router'

type BetaState =
  | 'loading'
  | 'offered'
  | 'submitting'
  | 'accepted'
  | 'alreadyJoined'
  | 'declined'
  | 'expired'
  | 'error'

type Provider = 'google' | 'apple'
type BetaAction = 'load' | 'join' | 'decline'

const terminalCopy: Partial<Record<BetaState, { title: Parameters<typeof t>[0]; body: Parameters<typeof t>[0] }>> = {
  accepted: { title: 'betaAccess.accepted.title', body: 'betaAccess.accepted.body' },
  alreadyJoined: { title: 'betaAccess.alreadyJoined.title', body: 'betaAccess.alreadyJoined.body' },
  declined: { title: 'betaAccess.declined.title', body: 'betaAccess.declined.body' },
  expired: { title: 'betaAccess.expired.title', body: 'betaAccess.expired.body' },
  error: { title: 'betaAccess.error.title', body: 'betaAccess.error.body' },
}

function StatusIcon({ positive }: { positive: boolean }) {
  return (
    <span className={`beta-status-icon${positive ? ' beta-status-icon-positive' : ''}`} aria-hidden="true">
      {positive ? '✓' : '•'}
    </span>
  )
}

/** Restricted beta-offer consent. No email or capability enters JavaScript. */
export function BetaAccessScreen() {
  const navigate = useNavigate()
  const [state, setState] = useState<BetaState>('loading')
  const [provider, setProvider] = useState<Provider | null>(null)
  const [consent, setConsent] = useState(false)
  const [failedAction, setFailedAction] = useState<BetaAction | null>(null)

  const loadOffer = useCallback(async (signal?: AbortSignal) => {
    setState('loading')
    setFailedAction(null)
    try {
      const view = await api.betaNotifications.get(signal)
      setProvider(view.provider)
      setState('offered')
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return
      const failure = e instanceof ApiFailure ? e : null
      if (failure?.body.code === 'betaOfferExpired' || failure?.status === 401) {
        setState('expired')
      } else {
        setFailedAction('load')
        setState('error')
      }
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadOffer(controller.signal)
    return () => controller.abort()
  }, [loadOffer])

  const join = () => {
    if (!consent || state === 'submitting') return
    setState('submitting')
    setFailedAction(null)
    void api.betaNotifications.join().then((result) => setState(result.status)).catch((e: unknown) => {
      const failure = e instanceof ApiFailure ? e : null
      if (failure?.body.code === 'betaOfferExpired' || failure?.status === 401) setState('expired')
      else {
        setFailedAction('join')
        setState('error')
      }
    })
  }

  const decline = () => {
    if (state === 'submitting') return
    setState('submitting')
    setFailedAction(null)
    void api.betaNotifications.decline().then(() => setState('declined')).catch((e: unknown) => {
      const failure = e instanceof ApiFailure ? e : null
      if (failure?.body.code === 'betaOfferExpired' || failure?.status === 401) setState('expired')
      else {
        setFailedAction('decline')
        setState('error')
      }
    })
  }

  const terminal = terminalCopy[state]
  const hasOffer = provider !== null
  const offered = state === 'offered' || state === 'submitting' || (state === 'error' && hasOffer)
  const loadError = state === 'error' && failedAction === 'load'
  const positive = state === 'accepted' || state === 'alreadyJoined'
  const providerName = provider ? t(`signInMethods.provider.${provider}`) : ''
  const finish = () => navigate('welcome', {}, { replace: true })
  const signInAgain = () => navigate('signIn', {}, { replace: true })
  const retry = () => {
    if (failedAction === 'load') void loadOffer()
    else if (failedAction === 'decline') decline()
    else join()
  }

  return (
    <Screen id="betaAccess" state={state} chrome="standalone">
      <div className="auth-panel beta-panel" aria-live="polite">
        {state === 'loading' ? (
          <LoadingRegion>
            <Skeleton width="var(--auth-beta-status-size)" height="var(--auth-beta-status-size)" />
            <SkeletonText lines={3} />
            <Skeleton height="var(--layout-minTapTarget)" />
          </LoadingRegion>
        ) : (
          <div className="stack">
            <StatusIcon positive={positive} />
            <div className="auth-heading">
              <h1 className="page-title">{terminal ? t(terminal.title) : t('betaAccess.title')}</h1>
              {state !== 'error' ? (
                <p className="body-secondary">
                  {terminal ? t(terminal.body) : t('betaAccess.body', { provider: providerName })}
                </p>
              ) : null}
            </div>

            {loadError ? (
              <div className="stack-sm">
                <InlineError>{t('betaAccess.error.body')}</InlineError>
                <Button variant="primary" block onClick={retry}>
                  {t('cta.retry')}
                </Button>
              </div>
            ) : offered ? (
              <>
                <label className="consent-row">
                  <input
                    type="checkbox"
                    checked={consent}
                    disabled={state === 'submitting'}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  <span>{t('betaAccess.consent')}</span>
                </label>
                {state === 'error' ? <InlineError>{t('betaAccess.error.body')}</InlineError> : null}
                <div className="stack-sm beta-actions">
                  <Button
                    variant="primary"
                    block
                    busy={state === 'submitting'}
                    showBusyLabel
                    disabled={(state !== 'error' || failedAction === 'join') && (!consent || state === 'submitting')}
                    onClick={state === 'error' ? retry : join}
                  >
                    {state === 'submitting'
                      ? t('betaAccess.cta.joining')
                      : state === 'error'
                        ? t('cta.retry')
                        : t('betaAccess.cta.join')}
                  </Button>
                  {failedAction !== 'decline' ? (
                    <Button variant="quiet" block disabled={state === 'submitting'} onClick={decline}>
                      {t('betaAccess.cta.notNow')}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <Button
                variant="primary"
                block
                onClick={state === 'expired' ? signInAgain : finish}
              >
                {state === 'expired' ? t('betaAccess.cta.signInAgain') : t('betaAccess.cta.done')}
              </Button>
            )}
          </div>
        )}
      </div>
    </Screen>
  )
}
